import { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';
import { ProLock, UpgradeDialog, UpgradeButtons } from '../components/ProUpgrade';
import { Textarea } from '../components/ui/textarea';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from '../components/ui/alert-dialog';
import PlanBadge from '../components/PlanBadge';
import VoicePicker from '../components/VoicePicker';
import { Volume2, Target, Flame, Bell, ChevronDown, Sparkles, Download, Share, Check, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import {
    SPEED_OPTIONS, SPEED_LABELS, getSavedSpeed, saveSpeed,
    getSavedPlayEnglish, savePlayEnglish,
    getSavedFlow, saveFlow,
    getSavedOrder, saveOrder,
    SESSION_LENGTH_OPTIONS, getSavedSessionLength, saveSessionLength,
} from '../lib/speech';
import { cancelFrench, loadTTSVoices, getSavedTTSVoice, saveTTSVoice } from '../lib/tts';
import { settingsApi, billingApi } from '../lib/api';
import { PLAN_PRICES, PRICING_URL, canCheckout } from '../lib/billing';
import { useAuth } from '../contexts/AuthContext';
import { markChecklistDone } from '../lib/onboardingChecklist';
import { useInstallPrompt } from '../lib/installPrompt';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const WEEKLY_TARGET_OPTIONS = [1, 3, 5, 7];
const SESSION_LABEL = { 5: '5 min', 10: '10 min', 15: '15 min', 20: '20 min', 30: '30 min' };
const VOICE_PREVIEW_TEXT = 'Bonjour, voici un aperçu de cette voix.';

const SectionHeader = ({ icon: Icon, color, title, subtitle, badge = null }) => (
    <div className="flex items-center gap-3 mb-6">
        <div className={`w-10 h-10 ${color} rounded-xl flex items-center justify-center`}>
            <Icon className="h-5 w-5" />
        </div>
        <div>
            <h2 className="flex items-center gap-2 text-base font-semibold text-foreground">
                {title}
                {badge}
            </h2>
            {subtitle && <p className="text-sm text-stone-500">{subtitle}</p>}
        </div>
    </div>
);


// Permanent home for the install option. The banner is dismissable and, once
// dismissed, gone for good, so anyone who decides later that they do want the
// icon needs somewhere to find it.
const InstallCard = () => {
    const { canInstall, needsIosInstructions, installed, promptInstall } = useInstallPrompt();

    const header = (
        <SectionHeader
            icon={Download}
            color="bg-emerald-100 text-emerald-600"
            title="Add Mimique to home screen"
            subtitle="The full app on your home screen, straight to your practice"
        />
    );

    return (
        <Card className="bg-white border-stone-100 shadow-sm break-inside-avoid mb-5 md:mb-6">
            <CardContent className="p-4 md:p-6">
                {/* With the button gone, the download icon carries the action:
                    where the browser can install, the whole header is the
                    control. Everywhere else it stays plain text. */}
                {!installed && canInstall ? (
                    <button
                        onClick={promptInstall}
                        className="block w-full text-left rounded-xl -m-1 p-1 hover:bg-stone-50 transition-colors"
                    >
                        {header}
                    </button>
                ) : header}

                {installed && (
                    <p className="flex items-center gap-2 text-sm text-stone-600">
                        <Check className="h-4 w-4 text-emerald-600" />
                        Mimique is on this device's home screen.
                    </p>
                )}
                {!installed && !canInstall && needsIosInstructions && (
                    <p className="text-sm text-stone-600 leading-relaxed">
                        Tap <Share className="inline h-4 w-4 -mt-0.5" aria-label="Share" /> Share (inside the
                        {' '}<strong>···</strong> button on newer iPhones), then <strong>Add to Home Screen</strong>.
                    </p>
                )}
                {/* Desktop browsers that can't install, or Chrome before it has
                    decided the app qualifies, land here. */}
                {!installed && !canInstall && !needsIosInstructions && (
                    <p className="text-sm text-stone-500 leading-relaxed">
                        Open Mimique on your phone to add it to your home screen. On a computer, look for
                        the install icon in your browser's address bar.
                    </p>
                )}
            </CardContent>
        </Card>
    );
};

// Why someone is leaving, in their words where they want to give them. Kept
// short and blame-free: the question is asked once, on the way out, and it is
// never what stands between them and cancelling.
const CANCEL_REASONS = [
    'It costs more than I want to spend',
    "I'm not using it enough",
    'Something was not working properly',
    "I've got what I needed from it",
    "I'm using something else",
    'Another reason',
];

// The way up, for anyone on Free: the two buttons and a link to the pricing
// table. What each plan includes is compared in one place, on the landing
// page, rather than half-repeated here.
const UpgradeOptions = () => (
    <div className="space-y-3">
        <UpgradeButtons />
        {/* New tab on purpose: comparing plans should not cost them Settings. */}
        {canCheckout() && (
            <a href={PRICING_URL} target="_blank" rel="noopener noreferrer"
               className="block text-center text-xs text-primary underline underline-offset-2">
                Compare the plans
            </a>
        )}
    </div>
);

// Your plan, and the way out. Cancelling has to be as easy as signing up was,
// so it lives here in plain sight rather than behind an email to support.
const PlanCard = () => {
    const { user, refreshUser } = useAuth();
    const [sub, setSub] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [downgradeOpen, setDowngradeOpen] = useState(false);
    // More than one thing is usually true at once ("not using it enough" AND
    // "something was not working"), so this is checkboxes, not a single pick.
    const [reasons, setReasons] = useState([]);
    const [detail, setDetail] = useState('');
    const [working, setWorking] = useState(false);

    const load = () => {
        billingApi.getSubscription()
            .then(res => setSub(res.data))
            .catch(() => setSub(null))
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    const prettyDate = (iso) => {
        if (!iso) return '';
        try {
            return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
        } catch {
            return '';
        }
    };

    const doCancel = async () => {
        setWorking(true);
        try {
            const res = await billingApi.cancel(reasons.join('; '), detail);
            setDialogOpen(false);
            setReasons([]);
            setDetail('');
            toast.success(sub?.challenge_pending
                ? "Cancelled. Your challenge place is released and you won't be charged."
                : res.data.ends_at
                ? `Cancelled. You keep everything until ${prettyDate(res.data.ends_at)}.`
                : 'Cancelled. You keep everything until the end of the period you have paid for.');
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not cancel just now. Please try again.');
        } finally {
            setWorking(false);
        }
    };

    // Unlimited to Pro. Nobody is asked why: they are staying, and the point of
    // the cheaper plan is that taking it should be easy.
    const doDowngrade = async () => {
        setWorking(true);
        try {
            await billingApi.downgrade();
            setDowngradeOpen(false);
            toast.success("You're on Pro now. Stripe has credited what was left of your Unlimited month.");
            await refreshUser();
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not change your plan just now. Please try again.');
        } finally {
            setWorking(false);
        }
    };

    const doResume = async () => {
        setWorking(true);
        try {
            await billingApi.resume();
            toast.success('Welcome back. Your subscription carries on as before.');
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not restart it just now. Please try again.');
        } finally {
            setWorking(false);
        }
    };

    const plan = (sub?.plan || user?.plan || 'free').toLowerCase();
    const planName = plan === 'unlimited' ? 'Unlimited' : plan === 'pro' ? 'Pro' : 'Free';

    return (
        <Card className="bg-white border-stone-100 shadow-sm break-inside-avoid mb-5 md:mb-6">
            <CardContent className="p-4 md:p-6">
                <SectionHeader
                    icon={CreditCard}
                    color="bg-stone-100 text-stone-600"
                    title="Your plan"
                    badge={<PlanBadge plan={plan} size="sm" />}
                />

                {loading ? (
                    <p className="text-sm text-stone-500">Checking your plan...</p>
                ) : sub?.challenge_pending ? (
                    // Paid for the 30-day challenge, on Free until it starts.
                    // The subscription is in trial, so cancelling costs nothing.
                    sub.cancel_at_period_end ? (
                        <>
                            <p className="text-sm text-stone-600 leading-relaxed">
                                You cancelled your place in the 30-day challenge, so you won't be charged.
                            </p>
                            <Button variant="outline" className="mt-4" onClick={doResume} disabled={working}>
                                Keep my challenge place
                            </Button>
                        </>
                    ) : (
                        <>
                            <p className="text-sm text-stone-600 leading-relaxed">
                                You have a place in the 30-day challenge. On {prettyDate(sub.challenge_starts)} you move to Unlimited, {PLAN_PRICES.unlimited}/month, and the challenge begins. Cancel before then and you won't be charged anything.
                            </p>
                            {sub.cancellable && (
                                <Button
                                    variant="outline"
                                    className="mt-4 w-full sm:w-auto text-stone-600"
                                    onClick={() => setDialogOpen(true)}
                                >
                                    Cancel my challenge place
                                </Button>
                            )}
                        </>
                    )
                ) : plan === 'free' ? (
                    // The upgrade lives on the page, not behind a button that
                    // opens a dialog to show the same thing. Somebody reading
                    // their plan is already asking what the paid ones give them.
                    <UpgradeOptions />
                ) : sub?.cancel_at_period_end ? (
                    <>
                        <p className="text-sm text-stone-600 leading-relaxed">
                            Your {planName} plan ends on {prettyDate(sub.ends_at)}. Nothing changes until then.
                        </p>
                        <Button variant="outline" className="mt-4" onClick={doResume} disabled={working}>
                            Keep my {planName} plan
                        </Button>
                    </>
                ) : sub?.cancellable ? (
                    <>
                        <p className="text-sm text-stone-600 leading-relaxed">
                            {sub?.renews_at ? `Renews on ${prettyDate(sub.renews_at)}.` : 'Billed monthly.'} Cancel any time.
                        </p>
                        {/* Stacked on a phone: these labels carry a price, and
                            side by side they ran off the edge of the card. */}
                        <div className="mt-4 flex flex-col sm:flex-row sm:flex-wrap gap-2">
                            {/* A cheaper plan is a better outcome than leaving, for
                                both sides, so it sits in front of the cancel button. */}
                            {/* Same green and same icon as the Pro button
                                everywhere else, so Pro looks like Pro whichever
                                direction you reach it from. */}
                            {plan === 'unlimited' && (
                                <Button className="w-full sm:w-auto" onClick={() => setDowngradeOpen(true)}>
                                    <Sparkles className="h-4 w-4" />
                                    Downgrade to Pro, {PLAN_PRICES.pro}/month
                                </Button>
                            )}
                            <Button
                                variant="outline"
                                className="w-full sm:w-auto text-stone-600"
                                onClick={() => setDialogOpen(true)}
                            >
                                Cancel my subscription
                            </Button>
                        </div>
                    </>
                ) : (
                    <p className="text-sm text-stone-600 leading-relaxed">
                        You're on {planName}. There's no payment set up on this account, so there's nothing to cancel.
                        Any questions, email hello@mimique.app.
                    </p>
                )}

                <AlertDialog open={downgradeOpen} onOpenChange={setDowngradeOpen}>
                    <AlertDialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
                        <AlertDialogHeader>
                            <AlertDialogTitle>Downgrade to Pro?</AlertDialogTitle>
                            <AlertDialogDescription asChild>
                                <div className="space-y-2 text-left text-stone-600">
                                    <p>
                                        You move to Pro at {PLAN_PRICES.pro} a month now. What is left of this month's
                                        Unlimited is credited against your next payments.
                                    </p>
                                    <p>
                                        Pro keeps the natural voices, the pronunciation feedback and AI phrases, with
                                        lower limits. Nothing you have added is removed, and you can go back to
                                        Unlimited whenever you like.
                                    </p>
                                </div>
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel disabled={working}>Stay on Unlimited</AlertDialogCancel>
                            <AlertDialogAction
                                onClick={(e) => { e.preventDefault(); doDowngrade(); }}
                                disabled={working}
                            >
                                {working ? 'Downgrading...' : 'Downgrade to Pro'}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>

                <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <AlertDialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
                        <AlertDialogHeader>
                            <AlertDialogTitle>Sorry to see you go</AlertDialogTitle>
                            <AlertDialogDescription asChild>
                                <div className="space-y-2 text-left text-stone-600">
                                    {sub?.challenge_pending ? (
                                        <p>
                                            Your challenge place is released and you won't be charged. You stay on
                                            Free. Nothing is deleted.
                                        </p>
                                    ) : (
                                        <p>
                                            You keep {planName} until the end of the period you have paid for, then move to
                                            Free. Nothing is deleted.
                                        </p>
                                    )}
                                    <p>
                                        I'd love to know why you are leaving so I can make improvements.
                                    </p>
                                </div>
                            </AlertDialogDescription>
                        </AlertDialogHeader>

                        <div className="space-y-3">
                            <div className="space-y-2">
                                    {CANCEL_REASONS.map(r => (
                                        <label key={r} className="flex items-start gap-2 cursor-pointer rounded p-1 hover:bg-stone-50">
                                            <Checkbox
                                                className="mt-0.5"
                                                checked={reasons.includes(r)}
                                                onCheckedChange={() => setReasons(prev =>
                                                    prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]
                                                )}
                                            />
                                            <span className="text-sm text-stone-700 leading-snug">{r}</span>
                                        </label>
                                    ))}
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-sm">Anything to add?</Label>
                                <Textarea
                                    value={detail}
                                    onChange={(e) => setDetail(e.target.value)}
                                    rows={3}
                                    placeholder="Anything at all, good or bad"
                                />
                            </div>
                        </div>

                        <AlertDialogFooter>
                            <AlertDialogCancel disabled={working}>Keep my subscription</AlertDialogCancel>
                            <AlertDialogAction
                                onClick={(e) => { e.preventDefault(); doCancel(); }}
                                disabled={working}
                            >
                                {working ? 'Cancelling...' : 'Cancel subscription'}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </CardContent>
        </Card>
    );
};

const Settings = () => {
    const { user } = useAuth();
    // Tick off the Dashboard checklist's "Familiarise yourself with Settings"
    // item on first landing here. Idempotent per-user via the helper.
    useEffect(() => { markChecklistDone(user?.id, 'settings_visited'); }, [user?.id]);

    // ── Playback (localStorage) ──────────────────────────────────────────
    const [playbackSpeed, setPlaybackSpeed] = useState(getSavedSpeed);
    const [playEnglish, setPlayEnglish] = useState(getSavedPlayEnglish);
    const [practiceFlow, setPracticeFlow] = useState(getSavedFlow);
    const [practiceOrder, setPracticeOrder] = useState(getSavedOrder);
    const [sessionLengthTarget, setSessionLengthTarget] = useState(getSavedSessionLength);
    const [selectedVoice, setSelectedVoice] = useState(getSavedTTSVoice);
    const [ttsVoices, setTtsVoices] = useState([]);
    // Natural French voices are Pro and above: /api/tts/voices returns an
    // empty list on Free, which is why the picker has nothing to show.
    const isFree = (user?.plan || 'free') === 'free';
    const [upgradeOpen, setUpgradeOpen] = useState(false);

    // ── Remote settings ──────────────────────────────────────────────────
    const [formalityPreference, setFormalityPreference] = useState('informal');
    const [countdownEnabled, setCountdownEnabled] = useState(true);
    const [speakerGender, setSpeakerGender] = useState('male');
    const [frenchLevel, setFrenchLevel] = useState('beginner');
    const [weeklyTarget, setWeeklyTarget] = useState(5);
    const [streakFreeze, setStreakFreeze] = useState(true);
    const [restDays, setRestDays] = useState([]);
    const [reminderTime, setReminderTime] = useState('');
    const [reminderEnabled, setReminderEnabled] = useState(false);
    const settingsLoaded = useRef(false);

    // Stop any voice preview if the user leaves the page mid-playback.
    useEffect(() => () => { cancelFrench(); }, []);

    useEffect(() => {
        loadTTSVoices().then(setTtsVoices);
        settingsApi.get().then(res => {
            const s = res.data;
            setWeeklyTarget(s.weekly_target ?? 5);
            setStreakFreeze(s.streak_freeze_enabled ?? true);
            setRestDays(s.rest_days ?? []);
            if (s.reminder_time) { setReminderTime(s.reminder_time); setReminderEnabled(true); }
            if (s.session_length_target) {
                setSessionLengthTarget(s.session_length_target);
                saveSessionLength(s.session_length_target);
            }
            setFormalityPreference(s.formality_preference ?? 'informal');
            setCountdownEnabled(s.countdown_enabled ?? true);
            setSpeakerGender(s.speaker_gender ?? 'male');
            setFrenchLevel(s.french_level ?? 'beginner');
        }).catch(() => {}).finally(() => { settingsLoaded.current = true; });
    }, []);

    // ── Auto-save remote settings (debounced) ────────────────────────────
    useEffect(() => {
        if (!settingsLoaded.current) return;
        const t = setTimeout(() => {
            settingsApi.update({
                weekly_target: weeklyTarget,
                session_length_target: sessionLengthTarget,
                streak_freeze_enabled: streakFreeze,
                rest_days: restDays,
                reminder_time: reminderEnabled && reminderTime ? reminderTime : null,
                formality_preference: formalityPreference,
                countdown_enabled: countdownEnabled,
                speaker_gender: speakerGender,
                french_level: frenchLevel,
            }).catch(() => toast.error('Failed to save settings'));
        }, 600);
        return () => clearTimeout(t);
    }, [weeklyTarget, sessionLengthTarget, streakFreeze, restDays, reminderEnabled, reminderTime, formalityPreference, countdownEnabled, speakerGender, frenchLevel]);

    // ── Local handlers ───────────────────────────────────────────────────
    const handleSpeedChange = (s) => { setPlaybackSpeed(s); saveSpeed(s); };
    const handlePlayEnglishChange = (v) => { setPlayEnglish(v); savePlayEnglish(v); };
    const handleFlowChange = (v) => { setPracticeFlow(v); saveFlow(v); };
    const handleOrderChange = (v) => { setPracticeOrder(v); saveOrder(v); };
    const handleSessionLengthChange = (n) => { setSessionLengthTarget(n); saveSessionLength(n); };
    const handleVoiceChange = (v) => {
        setSelectedVoice(v);
        saveTTSVoice(v);
    };

    // Save a single AI generation setting immediately (no debounce risk on navigate-away)
    const saveAiSetting = (field, value) =>
        settingsApi.update({ [field]: value })
            .then(() => toast.success('Saved', { duration: 1200 }))
            .catch(() => toast.error('Failed to save settings'));

    const toggleRestDay = (idx) => {
        setRestDays(prev => prev.includes(idx) ? prev.filter(d => d !== idx) : [...prev, idx]);
    };

    const requestNotificationPermission = async () => {
        if (!('Notification' in window)) { toast.error('Browser notifications not supported'); return; }
        const perm = await Notification.requestPermission();
        if (perm === 'granted') toast.success('Notifications enabled');
        else toast.error('Notification permission denied');
    };

    return (
        <div className="min-h-screen bg-fixed bg-gradient-to-b from-foreground to-primary text-primary-foreground">
            <header className="sticky top-0 z-40 bg-foreground/90 backdrop-blur-md border-b border-primary-foreground/10">
                {/* Same max-width and padding as <main> below, so the title
                    starts on the same line as the cards. */}
                <div className="max-w-5xl mx-auto px-4 md:px-6 py-4">
                    <h1 className="text-xl font-bold text-primary-foreground">Settings</h1>
                    <p className="text-sm text-primary-foreground/70">Playback, goals & reminders</p>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-8">
                {/* CSS columns rather than a grid. The cards are five, and wildly
                    different heights: in a two-column grid an odd count always
                    leaves a hole, and every short card gets stretched or stranded
                    beside a tall one. Columns flow the cards on and balance the
                    two sides automatically, whatever each card ends up holding.
                    Spacing comes from mb on the cards, since column-gap only
                    separates the columns themselves. */}
                <div className="md:columns-2 md:gap-6">

                {/* ── Playback ────────────────────────────────────────── */}
                <Card className="bg-white border-stone-100 shadow-sm break-inside-avoid mb-5 md:mb-6">
                    <CardContent className="p-4 md:p-6">
                        <SectionHeader icon={Volume2} color="bg-primary/10 text-primary" title="Playback & Recordings" subtitle="Controls how phrases are practiced" />
                        <div className="space-y-6">
                            {/* Voice sits first: it is the setting people come here
                                to change, and for Free users it is also the clearest
                                reason to upgrade. */}
                            {ttsVoices.length > 0 ? (
                                <div className="space-y-3">
                                    <Label className="text-sm font-medium text-stone-700">Select French Voice</Label>
                                    <VoicePicker
                                        voices={ttsVoices}
                                        value={selectedVoice || ttsVoices[0].id}
                                        onChange={handleVoiceChange}
                                        speed={playbackSpeed}
                                        previewText={VOICE_PREVIEW_TEXT}
                                    />
                                </div>
                            ) : isFree ? (
                                /* /api/tts/voices returns an empty list on Free, so this
                                   is the Free state, not a broken one. The copy here used
                                   to tell people to install OS language packs, which is
                                   left over from the Web Speech days and would not have
                                   helped them at all. */
                                <div className="space-y-3">
                                    <div className="flex items-center gap-2">
                                        <Label className="text-sm font-medium text-stone-700">Select French Voice</Label>
                                        <ProLock />
                                    </div>
                                    <div className="max-w-xs rounded-lg border border-stone-200 bg-stone-50 px-3 py-2.5 text-sm text-stone-400">
                                        Standard browser voice
                                    </div>
                                    <p className="text-xs leading-relaxed text-stone-500">
                                        Upgrade for a choice of natural French voices to choose from.{' '}
                                        <button
                                            type="button"
                                            className="text-primary underline underline-offset-2"
                                            onClick={() => setUpgradeOpen(true)}
                                        >
                                            Upgrade
                                        </button>
                                    </p>
                                </div>
                            ) : (
                                <p className="text-sm text-stone-400">No voices available right now. Playback falls back to your device's built-in voice.</p>
                            )}

                            <div className="space-y-2">
                                <Label className="text-sm font-medium text-stone-700">Practice Speed</Label>
                                <Select value={String(playbackSpeed)} onValueChange={v => handleSpeedChange(parseFloat(v))}>
                                    <SelectTrigger className="bg-stone-50 border-stone-200 max-w-xs">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {SPEED_OPTIONS.map(s => (
                                            <SelectItem key={s} value={String(s)}>{SPEED_LABELS[s] || `${s}x`}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-stone-500">Use this to control the listening and speaking speed</p>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-sm font-medium text-stone-700">Practice Order</Label>
                                    <Select value={practiceOrder} onValueChange={handleOrderChange}>
                                        <SelectTrigger data-testid="settings-order-select" className="bg-stone-50 border-stone-200">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="smart">Smart (low scores first)</SelectItem>
                                            <SelectItem value="random">Random (shuffled)</SelectItem>
                                            <SelectItem value="added">Order added (ascending)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-sm font-medium text-stone-700">Practice Flow</Label>
                                    <Select value={practiceFlow} onValueChange={handleFlowChange}>
                                        <SelectTrigger data-testid="settings-flow-select" className="bg-stone-50 border-stone-200">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="manual">Manual</SelectItem>
                                            <SelectItem value="automatic">Automatic (hands-free loop)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            {practiceFlow === 'automatic' && (
                                <>
                                    <div className="flex items-start justify-between gap-2">
                                        <div>
                                            <p className="text-sm font-medium text-stone-700">Recording Countdown</p>
                                            <p className="text-xs text-stone-400 mt-0.5">Show 3–2–1 before each recording window opens</p>
                                        </div>
                                        <Switch checked={countdownEnabled} onCheckedChange={setCountdownEnabled} />
                                    </div>
                                    <div className="flex items-start justify-between gap-2">
                                        <div>
                                            <p className="text-sm font-medium text-stone-700">Play English Translation</p>
                                            <p className="text-xs text-stone-400 mt-0.5">Read the English meaning of each phrase aloud</p>
                                        </div>
                                        <Switch checked={playEnglish} onCheckedChange={handlePlayEnglishChange} />
                                    </div>
                                </>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* ── AI Generation ───────────────────────────────────── */}
                <Card className="bg-white border-stone-100 shadow-sm break-inside-avoid mb-5 md:mb-6">
                    <CardContent className="p-4 md:p-6">
                        <SectionHeader icon={Sparkles} color="bg-amber-100 text-amber-500" title="AI Generation" subtitle="Controls how AI-generated phrases are written" />
                        <div className="space-y-6">
                            <div className="space-y-2">
                                <Label className="text-sm font-medium text-stone-700">Phrase Style</Label>
                                <div className="flex gap-1 sm:gap-2 p-1 bg-stone-100 rounded-lg max-w-xs">
                                    <button
                                        type="button"
                                        className={`flex-1 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-all ${formalityPreference === 'informal' ? 'bg-white shadow-sm text-primary' : 'text-stone-500 hover:text-stone-700'}`}
                                        onClick={() => { setFormalityPreference('informal'); saveAiSetting('formality_preference', 'informal'); }}
                                    >
                                        Informal (tu)
                                    </button>
                                    <button
                                        type="button"
                                        className={`flex-1 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-all ${formalityPreference === 'formal' ? 'bg-white shadow-sm text-primary' : 'text-stone-500 hover:text-stone-700'}`}
                                        onClick={() => { setFormalityPreference('formal'); saveAiSetting('formality_preference', 'formal'); }}
                                    >
                                        Formal (vous)
                                    </button>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-sm font-medium text-stone-700">French Level</Label>
                                <div className="flex gap-1 sm:gap-2 p-1 bg-stone-100 rounded-lg max-w-xs">
                                    {['beginner', 'intermediate', 'advanced'].map(lvl => (
                                        <button
                                            key={lvl}
                                            type="button"
                                            className={`flex-1 py-1.5 text-xs sm:text-sm font-medium rounded-md capitalize transition-all ${frenchLevel === lvl ? 'bg-white shadow-sm text-primary' : 'text-stone-500 hover:text-stone-700'}`}
                                            onClick={() => { setFrenchLevel(lvl); saveAiSetting('french_level', lvl); }}
                                        >
                                            {lvl.charAt(0).toUpperCase() + lvl.slice(1)}
                                        </button>
                                    ))}
                                </div>
                                <p className="text-xs text-stone-400">Sets the default difficulty when AI-generating phrases. You can override it per generation.</p>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-sm font-medium text-stone-700">Your Gender</Label>
                                <div className="flex gap-1 sm:gap-2 p-1 bg-stone-100 rounded-lg max-w-xs">
                                    <button
                                        type="button"
                                        className={`flex-1 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-all ${speakerGender === 'male' ? 'bg-white shadow-sm text-primary' : 'text-stone-500 hover:text-stone-700'}`}
                                        onClick={() => { setSpeakerGender('male'); saveAiSetting('speaker_gender', 'male'); }}
                                    >
                                        Male
                                    </button>
                                    <button
                                        type="button"
                                        className={`flex-1 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-all ${speakerGender === 'female' ? 'bg-white shadow-sm text-primary' : 'text-stone-500 hover:text-stone-700'}`}
                                        onClick={() => { setSpeakerGender('female'); saveAiSetting('speaker_gender', 'female'); }}
                                    >
                                        Female
                                    </button>
                                </div>
                                <p className="text-xs text-stone-400">Used for correct French agreement (e.g. "allé" vs "allée") in generated phrases.</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* ── Goals & Streak ──────────────────────────────────── */}
                <Card className="bg-white border-stone-100 shadow-sm break-inside-avoid mb-5 md:mb-6">
                    <CardContent className="p-4 md:p-6">
                        <SectionHeader icon={Target} color="bg-emerald-100 text-emerald-600" title="Goals & Streak" subtitle="Targets and streak settings for your practice habit" />
                        <div className="space-y-6">
                            <div className="space-y-2">
                                <Label className="text-sm font-medium text-stone-700">Daily Practice Goal</Label>
                                <Select value={String(sessionLengthTarget)} onValueChange={v => handleSessionLengthChange(parseInt(v))}>
                                    <SelectTrigger className="bg-stone-50 border-stone-200 max-w-xs">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {SESSION_LENGTH_OPTIONS.map(n => (
                                            <SelectItem key={n} value={String(n)}>{SESSION_LABEL[n]}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-sm font-medium text-stone-700">Weekly Practice Target</Label>
                                <Select value={String(weeklyTarget)} onValueChange={v => setWeeklyTarget(parseInt(v))}>
                                    <SelectTrigger className="bg-stone-50 border-stone-200 max-w-xs">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {WEEKLY_TARGET_OPTIONS.map(n => (
                                            <SelectItem key={n} value={String(n)}>{n} day{n !== 1 ? 's' : ''}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-stone-400">How many days per week you aim to practise.</p>
                            </div>
                            <hr className="border-stone-100" />
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-stone-700">Streak Freeze</p>
                                    <p className="text-xs text-stone-400 mt-0.5">Forgive one missed non-rest day before breaking your streak</p>
                                </div>
                                <Switch checked={streakFreeze} onCheckedChange={setStreakFreeze} />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-sm font-medium text-stone-700">Rest Days</Label>
                                <p className="text-xs text-stone-400">Nominated rest days are skipped in streak calculations — missing them never breaks your streak.</p>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <button className="flex items-center justify-between w-full max-w-xs px-3 py-2 bg-stone-50 border border-stone-200 rounded-lg text-sm text-stone-700 hover:bg-stone-100 transition-colors text-left">
                                            <span>{restDays.length === 0 ? 'No rest days' : restDays.map(i => DAYS[i]).join(', ')}</span>
                                            <ChevronDown className="h-4 w-4 text-stone-400 ml-2 flex-shrink-0" />
                                        </button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-44 p-3" align="start">
                                        <div className="space-y-2">
                                            {DAYS.map((day, idx) => (
                                                <label key={day} className="flex items-center gap-2 cursor-pointer rounded p-1 hover:bg-stone-50">
                                                    <Checkbox checked={restDays.includes(idx)} onCheckedChange={() => toggleRestDay(idx)} />
                                                    <span className="text-sm text-stone-700">{day}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </PopoverContent>
                                </Popover>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* ── Reminders ───────────────────────────────────────── */}
                <Card className="bg-white border-stone-100 shadow-sm break-inside-avoid mb-5 md:mb-6">
                    <CardContent className="p-4 md:p-6">
                        <SectionHeader icon={Bell} color="bg-amber-100 text-amber-500" title="Reminders" subtitle="A daily nudge to keep your practice on track" />
                        <div className="space-y-5">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-stone-700">Daily Email Reminder</p>
                                    <p className="text-xs text-stone-400 mt-0.5">A short email if you haven't practised that day</p>
                                </div>
                                <Switch
                                    checked={reminderEnabled}
                                    onCheckedChange={(v) => {
                                        setReminderEnabled(v);
                                        if (v && !reminderTime) setReminderTime('19:00');
                                    }}
                                />
                            </div>
                            {/* Time picker hidden until per-user scheduling exists — cron runs once
                                daily at 9am French time for everyone with reminder_time set. The
                                Reminders on/off switch stays wired: flipping on saves 19:00 as a
                                default reminder_time so the cron picks up the user. */}
                        </div>
                    </CardContent>
                </Card>

                <InstallCard />

                <PlanCard />

                </div>

                <UpgradeDialog
                    open={upgradeOpen}
                    onOpenChange={setUpgradeOpen}
                    title="Real French voices"
                    message="Free uses your device's own built-in voice, the robotic one."
                />

            </main>
        </div>
    );
};

export default Settings;
